import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  SearchField,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import PersonIcon from '../../../ui/icons/PersonIcon';
import { useDispatch } from 'react-redux';
import { useToast, useFormatters } from '../../../hooks';
import { transferRide as apiTransferRide } from '../../../redux/Trips/actions';
import api from '../../../lib/api';

// A readable one-line label for a user option / the selected user.
const userLabel = (user) => {
  if (!user || typeof user === 'string') return user || '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const contact = user.email || user.phoneNumber || user.id;
  return name ? `${name} (${contact})` : contact;
};

const TransferRideDialog = ({ open, onClose, ride, onSuccess }) => {
  const rideId = ride.id || ride.rideId;
  const t = useToast();
  const f = useFormatters();
  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setInputValue('');
      setOptions([]);
      setLoading(false);
      setSelectedUser(null);
      setSubmitting(false);
    }
  }, [open]);

  // Debounced user search (email / phone / name / id), mirroring UsersSearch.
  useEffect(() => {
    if (!open) return undefined;
    const q = inputValue.trim();
    if (!q) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const users = await api.search.searchUsers({ q });
        setOptions(users || []);
      } catch (err) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue, open]);

  const sameAsCurrentRider =
    selectedUser && (selectedUser.id === ride.rider.id || selectedUser.id === ride.rider.rider_id);

  const submit = async () => {
    if (!selectedUser || sameAsCurrentRider) return;
    setSubmitting(true);
    const result = await apiTransferRide(rideId, selectedUser.id)(dispatch);
    if (result.success) {
      t.success(`Ride transferred to ${userLabel(selectedUser)}`);
      onSuccess?.();
      onClose();
    } else {
      t.error(result.error || 'Failed to transfer ride');
      setSubmitting(false);
    }
  };

  const currentRiderName =
    [ride.rider.firstName, ride.rider.lastName].filter(Boolean).join(' ') || 'Unknown';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box display="flex" alignItems="center" justifyContent="space-between" px="24px" pt="24px">
        <DialogTitle sx={{ padding: 0 }}>Transfer ride to another rider</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack spacing="16px">
          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              Current rider
            </Typography>
            <Typography>{currentRiderName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {f.phone.toInternational(ride.rider.phoneNumber)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              Transfer to
            </Typography>
            <SearchField
              id="transfer_ride_user_search"
              sx={{ width: '100%' }}
              placeholder="Search by email, phone, name, or user ID"
              value={selectedUser}
              inputValue={inputValue}
              options={options}
              loading={loading}
              getOptionLabel={userLabel}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              onChange={(event, value) =>
                setSelectedUser(value && typeof value === 'object' ? value : null)
              }
              renderOption={(props, user) => (
                <Box component="li" key={user.id} {...props}>
                  <PersonIcon sx={{ fontSize: 14 }} />
                  <Typography component="span" sx={{ marginLeft: '10px' }}>
                    {user.email || user.phoneNumber}
                  </Typography>
                  <Typography component="span" color="text.secondary" sx={{ marginLeft: '8px' }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                </Box>
              )}
              blurOnSelect
            />
          </Box>

          {selectedUser && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected rider
              </Typography>
              <Typography>{userLabel(selectedUser)}</Typography>
            </Box>
          )}

          {sameAsCurrentRider && (
            <Alert severity="warning">
              <Typography variant="body2">
                That is the ride&apos;s current rider. Choose a different account.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: '24px', pb: '24px' }}>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedUser || sameAsCurrentRider || submitting}
          onClick={submit}
        >
          {submitting ? <CircularProgress size={20} /> : 'Transfer ride'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferRideDialog;
